import React, { useEffect, useState } from 'react'
import { collection, query, where, orderBy, onSnapshot, updateDoc, addDoc, doc } from 'firebase/firestore'
import { db } from '../../firebase.config'
import { useAuth } from '../../context/AuthContext'
import AppLayout from '../../components/layout/AppLayout'

const STATUS_COLORS: Record<string, string> = { pending: '#f59e0b', approved: '#16a34a', rejected: '#dc2626', submitted: '#7c3aed' }
const STATUS_BG: Record<string, string> = { pending: '#fffbeb', approved: '#f0fdf4', rejected: '#fef2f2', submitted: '#f5f3ff' }

// Week starts on Monday
function getWeekDates(weekOffset = 0): Date[] {
  const now = new Date()
  const day = now.getDay()
  const diff = now.getDate() - day + (day === 0 ? -6 : 1) // adjust for Sunday
  const monday = new Date(now.setDate(diff + weekOffset * 7))
  monday.setHours(0, 0, 0, 0)
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday)
    d.setDate(monday.getDate() + i)
    d.setHours(0, 0, 0, 0)
    return d
  })
}

function formatShort(d: Date) {
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
}

function formatDbDate(d: Date) {
  return d.toISOString().split('T')[0]
}

const PROJECTS = ['Project Alpha', 'Project Beta', 'Project Gamma']
const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

interface DayEntry {
  straightHours: string
  overtimeHours: string
  jobNumber: string
  notes: string
}

const EMPTY_WEEK = (): DayEntry[] => DAYS.map(() => ({ straightHours: '', overtimeHours: '', jobNumber: '', notes: '' }))

function calcTotal(entries: DayEntry[], field: 'straightHours' | 'overtimeHours'): number {
  return entries.reduce((sum, e) => sum + (parseFloat(e[field]) || 0), 0)
}

export default function TimesheetsPage() {
  const { orgId, userData, isSupervisor, isForemanOrAbove, isAdmin } = useAuth()
  const [timesheets, setTimesheets] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [weekOffset, setWeekOffset] = useState(0)
  const [selected, setSelected] = useState<any>(null)
  const [rejectingFor, setRejectingFor] = useState<any>(null)
  const [rejectReason, setRejectReason] = useState('')

  const weekDates = getWeekDates(weekOffset)
  const weekStart = weekDates[0]
  const weekEnd = weekDates[6]

  useEffect(() => {
    if (!orgId) return
    const unsub = onSnapshot(
      query(collection(db, 'organizations', orgId, 'timesheets'), where('orgId', '==', orgId), orderBy('createdAt', 'desc')),
      snap => { setTimesheets(snap.docs.map(d => ({ id: d.id, ...d.data() }))); setLoading(false) }
    )
    return () => unsub()
  }, [orgId])

  const myTimesheet = (weekStart: Date) => {
    const weekStr = formatDbDate(weekStart)
    return timesheets.find(t => t.userId === userData?.uid && t.weekOf === weekStr)
  }

  const myPending = myTimesheet(weekStart)

  const canEdit = (ts: any) => !ts || (ts.status !== 'approved' && ts.status !== 'submitted')
  const canSubmit = (ts: any) => ts && (ts.status === 'draft' || ts.status === 'rejected' || ts.status === 'pending')
  const canApprove = (ts: any) => {
    if (!ts || !isSupervisor()) return false
    // ts.submittedToRole is the role of the reviewer needed
    // e.g. member/foreman → submittedToRole: 'superintendant'
    // current user role must be >= submittedToRole (supervisors can approve)
    const roleOrder = ['member', 'foreman', 'superintendant', 'supervisor', 'admin']
    const reviewerRole = ts.submittedToRole || 'superintendant'
    const myRole = userData?.role || 'member'
    return roleOrder.indexOf(myRole) >= roleOrder.indexOf(reviewerRole)
  }

  const handleApprove = async (ts: any) => {
    const nextRole = escalateRole(ts.submittedToRole || 'superintendant')
    if (nextRole) {
      // Approve up — cascade to next level
      await updateDoc(doc(db, 'organizations', orgId, 'timesheets', ts.id), {
        status: 'submitted',
        submittedToRole: nextRole,
        updatedAt: new Date().toISOString()
      })
    } else {
      // Final approval
      await updateDoc(doc(db, 'organizations', orgId, 'timesheets', ts.id), {
        status: 'approved',
        updatedAt: new Date().toISOString()
      })
    }
  }

  const handleReject = async (ts: any, reason: string = '') => {
    await updateDoc(doc(db, 'organizations', orgId, 'timesheets', ts.id), {
      status: 'rejected',
      rejectionReason: reason,
      updatedAt: new Date().toISOString()
    })
  }

  const handleSubmit = async (ts: any) => {
    const toRole = getSubmittedToRole(userData?.role || 'member')
    if (ts.id) {
      await updateDoc(doc(db, 'organizations', orgId, 'timesheets', ts.id), {
        status: 'submitted',
        submittedToRole: toRole,
        updatedAt: new Date().toISOString()
      })
    }
  }

  // submittedToRole: who should review this timesheet
  const getSubmittedToRole = (userRole: string): string => {
    if (userRole === 'member') return 'foreman'
    if (userRole === 'foreman') return 'superintendant'
    if (userRole === 'superintendant') return 'supervisor'
    if (userRole === 'supervisor') return 'admin'
    return 'admin'
  }

  const escalateRole = (current: string): string | null => {
    const order = ['member', 'foreman', 'superintendant', 'supervisor', 'admin']
    const idx = order.indexOf(current)
    if (idx < 0 || idx >= order.length - 1) return null
    return order[idx + 1]
  }

  // Timesheets awaiting my review (where submittedToRole matches my role)
  const pendingMyReview = timesheets.filter(ts => {
    if (!isForemanOrAbove()) return false
    if (ts.status !== 'submitted') return false
    const myRole = userData?.role || 'member'
    const reviewerRole = ts.submittedToRole || 'superintendant'
    const roleOrder = ['member', 'foreman', 'superintendant', 'supervisor', 'admin']
    return roleOrder.indexOf(myRole) >= roleOrder.indexOf(reviewerRole)
  })

  // Build week data from timesheet entries
  const buildWeekEntries = (ts: any): DayEntry[] => {
    if (!ts?.entries) return EMPTY_WEEK()
    return ts.entries
  }

  const getTotalStraight = (entries: DayEntry[]) => calcTotal(entries, 'straightHours')
  const getTotalOvertime = (entries: DayEntry[]) => calcTotal(entries, 'overtimeHours')

  return (
    <AppLayout title="Timesheets">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => setWeekOffset(w => w - 1)}
            className="px-3 py-1.5 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 text-sm">
            ← Prev
          </button>
          <span className="font-semibold text-gray-800 text-sm">
            {weekStart.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })} – {weekEnd.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </span>
          <button onClick={() => setWeekOffset(w => w + 1)}
            className="px-3 py-1.5 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 text-sm">
            Next →
          </button>
          {weekOffset !== 0 && (
            <button onClick={() => setWeekOffset(0)} className="text-xs text-blue-600 hover:underline">This Week</button>
          )}
        </div>
        <p className="text-sm text-gray-500">{pendingMyReview.length} awaiting my review</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* My Timesheet — always visible weekly grid */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
              <h3 className="font-semibold text-gray-900 text-sm">My Timesheet — Week of {weekStart.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}</h3>
              {myPending ? (
                <span className="px-2 py-1 rounded text-xs font-medium" style={{ backgroundColor: STATUS_BG[myPending.status] || '#f5f5f5', color: STATUS_COLORS[myPending.status] || '#888' }}>
                  {myPending.status || 'draft'}
                </span>
              ) : (
                <span className="text-xs text-gray-400">No entry</span>
              )}
            </div>

            <WeekGrid
              weekDates={weekDates}
              entries={buildWeekEntries(myPending)}
              editable={canEdit(myPending)}
              mode="my"
              orgId={orgId}
              userData={userData}
              weekStart={formatDbDate(weekStart)}
              timesheetId={myPending?.id}
              status={myPending?.status}
            />
          </div>
        </div>

        {/* Status / Action Sidebar */}
        <div>
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            <h3 className="font-semibold text-gray-900 mb-4 text-sm">Status</h3>
            <div className="space-y-3 text-sm">
              {myPending ? (
                <>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Status:</span>
                    <span className="font-medium" style={{ color: STATUS_COLORS[myPending.status] }}>
                      {myPending.status === 'submitted' ? `Sent to ${myPending.submittedToRole}` : myPending.status || 'draft'}
                    </span>
                  </div>
                  {myPending.status === 'submitted' && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Awaiting:</span>
                      <span className="font-medium capitalize">{myPending.submittedToRole}</span>
                    </div>
                  )}
                  {myPending.status === 'rejected' && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-700">
                      <p className="font-semibold mb-1">Rejected — please correct and resubmit</p>
                      {myPending.rejectionReason && <p className="text-red-600">Reason: {myPending.rejectionReason}</p>}
                    </div>
                  )}
                  {myPending.status === 'approved' && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-xs text-green-700">
                      ✓ Approved — no further action needed
                    </div>
                  )}
                  {canSubmit(myPending) && (
                    <button
                      onClick={async () => {
                        const toRole = getSubmittedToRole(userData?.role || 'member')
                        if (myPending.id) {
                          await updateDoc(doc(db, 'organizations', orgId, 'timesheets', myPending.id), {
                            status: 'submitted',
                            submittedToRole: toRole,
                            updatedAt: new Date().toISOString()
                          })
                        } else {
                          // create first
                          const newTs = await addDoc(collection(db, 'organizations', orgId, 'timesheets'), {
                            orgId, userId: userData?.uid, userName: userData?.name || userData?.email,
                            weekOf: formatDbDate(weekStart),
                            status: 'submitted',
                            submittedToRole: toRole,
                            createdAt: new Date().toISOString(),
                            updatedAt: new Date().toISOString()
                          })
                        }
                      }}
                      className="w-full bg-blue-600 text-white py-2.5 rounded-lg text-sm font-medium hover:bg-blue-700 mt-2"
                    >
                      Submit for Approval
                    </button>
                  )}
                  {myPending.status === 'pending' && (
                    <p className="text-xs text-gray-500 italic">Save entries above to update your timesheet</p>
                  )}
                </>
              ) : (
                <p className="text-xs text-gray-400">Fill in the weekly grid and click Save to create your timesheet entry.</p>
              )}
            </div>
          </div>

          {/* Pending My Review */}
          {isSupervisor() && pendingMyReview.length > 0 && (
            <div className="mt-6 bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="px-5 py-3 border-b border-gray-200">
                <h3 className="font-semibold text-gray-900 text-sm">Pending My Review ({pendingMyReview.length})</h3>
              </div>
              <div className="divide-y divide-gray-100">
                {pendingMyReview.map(ts => (
                  <div key={ts.id} className="px-5 py-4">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-gray-900 text-sm">{ts.userName || 'Unknown'}</span>
                      <span className="text-xs text-gray-500">{ts.weekOf}</span>
                    </div>
                    <div className="text-xs text-gray-500 mb-2">
                      {calcTotal(buildWeekEntries(ts), 'straightHours').toFixed(1)}h straight + {calcTotal(buildWeekEntries(ts), 'overtimeHours').toFixed(1)}h OT
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => handleApprove(ts)} className="flex-1 bg-green-50 text-green-700 border border-green-200 py-1.5 rounded-lg text-xs font-medium hover:bg-green-100">
                        Approve ↑
                      </button>
                      <button onClick={() => setRejectingFor(ts)} className="flex-1 bg-red-50 text-red-700 border border-red-200 py-1.5 rounded-lg text-xs font-medium hover:bg-red-100">
                        Reject
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* All Timesheets Table (supervisor view) */}
      {isSupervisor() && (
        <div className="mt-8">
          <h3 className="font-semibold text-gray-900 mb-3">All Timesheets</h3>
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-5 py-3 font-medium text-gray-600">Employee</th>
                  <th className="text-left px-5 py-3 font-medium text-gray-600">Week Of</th>
                  <th className="text-left px-5 py-3 font-medium text-gray-600">ST Hours</th>
                  <th className="text-left px-5 py-3 font-medium text-gray-600">OT Hours</th>
                  <th className="text-left px-5 py-3 font-medium text-gray-600">Status</th>
                  <th className="text-left px-5 py-3 font-medium text-gray-600">Action</th>
                </tr>
              </thead>
              <tbody>
                {timesheets.map(t => (
                  <tr key={t.id} className="border-b border-gray-100 cursor-pointer hover:bg-gray-50" onClick={() => setSelected(t)}>
                    <td className="px-5 py-3 font-medium text-gray-900">{t.userName || 'Unknown'}</td>
                    <td className="px-5 py-3 text-gray-600">{t.weekOf || '-'}</td>
                    <td className="px-5 py-3 font-semibold text-blue-600">{calcTotal(buildWeekEntries(t), 'straightHours').toFixed(1)}h</td>
                    <td className="px-5 py-3 font-semibold text-purple-600">{calcTotal(buildWeekEntries(t), 'overtimeHours').toFixed(1)}h</td>
                    <td className="px-5 py-3">
                      <span className="px-2 py-1 rounded text-xs font-medium" style={{ backgroundColor: (STATUS_BG[t.status] || '#f5f5f5'), color: STATUS_COLORS[t.status] || '#888' }}>
                        {t.status === 'submitted' ? `→ ${t.submittedToRole}` : t.status || 'pending'}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      {t.status === 'submitted' && canApprove(t) && (
                        <div className="flex gap-2">
                          <button onClick={(e) => { e.stopPropagation(); handleApprove(t) }} className="text-xs text-green-600 hover:underline">Approve</button>
                          <button onClick={(e) => { e.stopPropagation(); setRejectingFor(t) }} className="text-xs text-red-600 hover:underline">Reject</button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Timesheet Detail Modal */}
      {selected && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{selected.userName || 'Unknown'}</h3>
                <p className="text-sm text-gray-500">Week of {selected.weekOf || '—'}</p>
              </div>
              <button onClick={() => setSelected(null)} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
            </div>

            {/* Status */}
            <div className="px-6 py-3 bg-gray-50 border-b border-gray-200 flex items-center gap-3">
              <span className="text-sm text-gray-600 font-medium">Status:</span>
              <span className="px-2 py-0.5 rounded text-xs font-medium" style={{ backgroundColor: STATUS_BG[selected.status] || '#f5f5f5', color: STATUS_COLORS[selected.status] || '#888' }}>
                {selected.status === 'submitted' ? `Submitted → ${selected.submittedToRole}` : selected.status || 'pending'}
              </span>
              {selected.status === 'submitted' && (
                <span className="text-xs text-gray-500">Awaiting {selected.submittedToRole} approval</span>
              )}
              {(selected.status === 'draft' || selected.status === 'pending') && (
                <span className="text-xs text-gray-500">Employee has not submitted for approval</span>
              )}
            </div>

            {/* Week Entries */}
            <div className="px-6 py-4">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium text-gray-600">Day</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-600">Job #</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-600">Straight</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-600">Overtime</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-600">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {buildWeekEntries(selected).map((e: DayEntry, idx: number) => (
                    <tr key={idx} className="border-b border-gray-100">
                      <td className="px-3 py-2 font-medium text-gray-800">{DAYS[idx]}</td>
                      <td className="px-3 py-2 text-gray-600">{e.jobNumber || '-'}</td>
                      <td className="px-3 py-2 text-blue-700 font-semibold">{e.straightHours || '0'}h</td>
                      <td className="px-3 py-2 text-purple-700 font-semibold">{e.overtimeHours || '0'}h</td>
                      <td className="px-3 py-2 text-gray-500 text-xs">{e.notes || '-'}</td>
                    </tr>
                  ))}
                  <tr className="bg-gray-50 border-t-2 border-gray-300">
                    <td className="px-3 py-2 font-semibold text-gray-800">Total</td>
                    <td className="px-3 py-2" />
                    <td className="px-3 py-2 font-bold text-blue-700">{calcTotal(buildWeekEntries(selected), 'straightHours').toFixed(1)}h</td>
                    <td className="px-3 py-2 font-bold text-purple-700">{calcTotal(buildWeekEntries(selected), 'overtimeHours').toFixed(1)}h</td>
                    <td className="px-3 py-2 font-bold text-gray-800">{(calcTotal(buildWeekEntries(selected), 'straightHours') + calcTotal(buildWeekEntries(selected), 'overtimeHours')).toFixed(1)}h</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Actions */}
            <div className="px-6 py-4 border-t border-gray-200 flex gap-3">
              {selected.status === 'submitted' && canApprove(selected) && (
                <>
                  <button
                    onClick={async () => { await handleApprove(selected); setSelected(null) }}
                    className="bg-green-600 text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-green-700"
                  >
                    Approve
                  </button>
                  <button
                    onClick={() => { setRejectingFor(selected); setSelected(null) }}
                    className="bg-red-600 text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-red-700"
                  >
                    Reject
                  </button>
                </>
              )}
              {canSubmit(selected) && (
                <button
                  onClick={async () => { await handleSubmit(selected); setSelected(null) }}
                  className="bg-blue-600 text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-blue-700"
                >
                  Submit for Approval
                </button>
              )}
              <button
                onClick={() => setSelected(null)}
                className="ml-auto border border-gray-300 text-gray-700 px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Reason Modal */}
      {rejectingFor && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setRejectingFor(null)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Reject Timesheet</h3>
              <p className="text-sm text-gray-500 mt-1">{rejectingFor.userName || 'Unknown'} — Week of {rejectingFor.weekOf || '—'}</p>
            </div>
            <div className="px-6 py-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Reason for rejection (required)</label>
              <textarea
                value={rejectReason}
                onChange={e => setRejectReason(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm"
                rows={3}
                placeholder="e.g., Missing overtime hours, incorrect job number..."
                autoFocus
              />
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex gap-3">
              <button
                onClick={async () => {
                  if (!rejectReason.trim()) return
                  await handleReject(rejectingFor, rejectReason)
                  setRejectingFor(null)
                  setRejectReason('')
                }}
                disabled={!rejectReason.trim()}
                className="bg-red-600 text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-red-700 disabled:bg-red-300"
              >
                Reject
              </button>
              <button
                onClick={() => { setRejectingFor(null); setRejectReason('') }}
                className="border border-gray-300 text-gray-700 px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  )
}

// WeekGrid component — always visible, always editable unless submitted
function WeekGrid({ weekDates, entries, editable, mode, orgId, userData, weekStart, timesheetId, status }: {
  weekDates: Date[]
  entries: DayEntry[]
  editable: boolean
  mode: 'my'
  orgId: string
  userData: any
  weekStart: string
  timesheetId?: string
  status?: string
}) {
  const [localEntries, setLocalEntries] = useState<DayEntry[]>(entries)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  // Sync when entries prop changes (e.g., week navigation)
  useEffect(() => { setLocalEntries(entries) }, [entries])

  const updateEntry = (dayIdx: number, field: keyof DayEntry, value: string) => {
    setLocalEntries(prev => prev.map((e, i) => i === dayIdx ? { ...e, [field]: value } : e))
    setSaved(false)
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      if (timesheetId) {
        const { updateDoc, doc } = await import('firebase/firestore')
        await updateDoc(doc(db, 'organizations', orgId, 'timesheets', timesheetId), {
          entries: localEntries,
          status: 'pending',
          updatedAt: new Date().toISOString()
        })
      } else {
        const { addDoc, collection } = await import('firebase/firestore')
        await addDoc(collection(db, 'organizations', orgId, 'timesheets'), {
          orgId,
          userId: userData?.uid,
          userName: userData?.name || userData?.email || 'unknown',
          weekOf: weekStart,
          entries: localEntries,
          status: 'pending',
          submittedToRole: null,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        })
      }
      setSaved(true)
    } finally {
      setSaving(false)
    }
  }

  const totalST = calcTotal(localEntries, 'straightHours')
  const totalOT = calcTotal(localEntries, 'overtimeHours')
  const totalAll = totalST + totalOT

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 border-b border-gray-200">
          <tr>
            <th className="text-left px-4 py-2 font-medium text-gray-600 w-10">Day</th>
            <th className="text-left px-4 py-2 font-medium text-gray-600">Job #</th>
            <th className="text-left px-4 py-2 font-medium text-gray-600">Straight Time</th>
            <th className="text-left px-4 py-2 font-medium text-gray-600">Overtime</th>
            <th className="text-left px-4 py-2 font-medium text-gray-600">Notes</th>
          </tr>
        </thead>
        <tbody>
          {weekDates.map((date, idx) => {
            const isWeekend = idx >= 5
            return (
              <tr key={idx} className={`border-b border-gray-100 ${isWeekend ? 'bg-gray-50' : ''}`}>
                <td className="px-4 py-2">
                  <div className={`font-medium ${isWeekend ? 'text-gray-400' : 'text-gray-800'}`}>
                    {DAYS[idx]}
                  </div>
                  <div className="text-xs text-gray-400">{date.getDate()}</div>
                </td>
                <td className="px-4 py-2">
                  {editable ? (
                    <input
                      type="text"
                      value={localEntries[idx].jobNumber}
                      onChange={e => updateEntry(idx, 'jobNumber', e.target.value)}
                      className="w-full border border-gray-200 rounded px-2 py-1 text-xs"
                      placeholder="Job #"
                    />
                  ) : (
                    <span className="text-gray-600 text-xs">{localEntries[idx].jobNumber || '-'}</span>
                  )}
                </td>
                <td className="px-4 py-2">
                  {editable ? (
                    <input
                      type="number"
                      value={localEntries[idx].straightHours}
                      onChange={e => updateEntry(idx, 'straightHours', e.target.value)}
                      className="w-full border border-gray-200 rounded px-2 py-1 text-xs"
                      placeholder="0.0"
                      min="0"
                      step="0.1"
                    />
                  ) : (
                    <span className="text-gray-800 font-medium">{localEntries[idx].straightHours || '-'}</span>
                  )}
                </td>
                <td className="px-4 py-2">
                  {editable ? (
                    <input
                      type="number"
                      value={localEntries[idx].overtimeHours}
                      onChange={e => updateEntry(idx, 'overtimeHours', e.target.value)}
                      className="w-full border border-gray-200 rounded px-2 py-1 text-xs"
                      placeholder="0.0"
                      min="0"
                      step="0.1"
                    />
                  ) : (
                    <span className="text-gray-800 font-medium">{localEntries[idx].overtimeHours || '-'}</span>
                  )}
                </td>
                <td className="px-4 py-2">
                  {editable ? (
                    <input
                      type="text"
                      value={localEntries[idx].notes}
                      onChange={e => updateEntry(idx, 'notes', e.target.value)}
                      className="w-full border border-gray-200 rounded px-2 py-1 text-xs"
                      placeholder="Notes"
                    />
                  ) : (
                    <span className="text-gray-500 text-xs">{localEntries[idx].notes || '-'}</span>
                  )}
                </td>
              </tr>
            )
          })}
          {/* Totals row */}
          <tr className="bg-gray-100 border-t-2 border-gray-300">
            <td className="px-4 py-2 font-semibold text-gray-800">Total</td>
            <td className="px-4 py-2" />
            <td className="px-4 py-2 font-semibold text-blue-700">{totalST.toFixed(1)}h</td>
            <td className="px-4 py-2 font-semibold text-purple-700">{totalOT.toFixed(1)}h</td>
            <td className="px-4 py-2 font-semibold text-gray-800">{totalAll.toFixed(1)}h total</td>
          </tr>
        </tbody>
      </table>

      {editable && (
        <div className="px-5 py-3 border-t border-gray-200 flex items-center gap-3">
          <button
            onClick={handleSave}
            disabled={saving}
            className="bg-accent text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-amber-700 disabled:bg-amber-300"
          >
            {saving ? 'Saving...' : saved ? '✓ Saved' : 'Save Week'}
          </button>
          {saved && <span className="text-xs text-green-600">Changes saved locally — click Submit when ready</span>}
        </div>
      )}
    </div>
  )
}
